import { useEffect, useState } from 'react';
import { log } from '@/logger/logger';
import { MAX_IMAGE_DIMENSION, clampedImageSize, decodeClampedImage } from '@/components/Recipe/resourceLoading';

const logger = log.getLogger('images');

/** WebP rather than PNG: a 4k re-encode is on the critical path to first paint. */
const REENCODE_TYPE = 'image/webp';
const REENCODE_QUALITY = 0.9;

/**
 * The URL to draw for `url`, re-encoded smaller when the source is far larger than it can
 * ever be displayed at.
 *
 * React Flow zooms with a CSS transform on a layer it never promotes, and Chrome keys its
 * image decode cache on raster scale, so every zoom step re-decodes each visible `<img>`
 * from its source bytes. Shrinking those bytes is what makes it cheap — the decode still
 * happens on every step, it just costs a fraction as much.
 *
 * Returns `undefined` only before anything has been painted, so the caller shows its
 * placeholder rather than the full-resolution image this exists to avoid. Once a URL has been
 * handed out it is held through later `url` changes — `url` changes under a mounted viewer when
 * asset resolution lands, and returning to `undefined` would clear an `<img>`'s `src` and blank
 * a tile that was already showing something.
 *
 * `sourceSize` is the extent of `url` itself. Images already within the ceiling are returned
 * untouched and cost nothing, as is an omitted `sourceSize` — pass none when the URL is known
 * to be small or its size is unknown, since guessing wrong costs a fetch and a decode to find
 * out, and leaves the caller with nothing to paint meanwhile.
 */
export function useDownscaledImageUrl(url: string, sourceSize?: { width: number; height: number }): string | undefined {
  const oversized =
    sourceSize !== undefined &&
    clampedImageSize(sourceSize.width, sourceSize.height, MAX_IMAGE_DIMENSION) !== undefined;
  // The most recent URL that was safe to paint, deliberately not reset when `url` changes.
  const [painted, setPainted] = useState<{ url: string; isObjectUrl: boolean }>();

  useEffect(() => {
    if (!oversized) {
      // Recorded even though it is returned directly below, so that a later switch to an
      // oversized source has something to hold on to while it re-encodes.
      setPainted({ url, isObjectUrl: false });
      return;
    }
    let cancelled = false;

    void downscale(url).then((result) => {
      if (cancelled) {
        // Never painted, so the effect below will not be the one to release it.
        if (result) {
          URL.revokeObjectURL(result);
        }
        return;
      }
      // Falling back to the source on failure: a slow image beats a missing one, and the
      // asset metadata that flagged this as oversized can simply be wrong.
      setPainted(result ? { url: result, isObjectUrl: true } : { url, isObjectUrl: false });
    });

    return () => {
      cancelled = true;
    };
  }, [url, oversized]);

  // Released once something else has been painted rather than when `url` changes: a blob is
  // still the one on screen for as long as its replacement takes to encode.
  useEffect(() => {
    if (!painted?.isObjectUrl) {
      return;
    }
    const objectUrl = painted.url;
    return () => URL.revokeObjectURL(objectUrl);
  }, [painted]);

  return oversized ? painted?.url : url;
}

/**
 * Re-encodes `url` under the ceiling, or resolves undefined to mean "draw the source
 * instead" — either because it needs no shrinking or because the attempt failed.
 */
async function downscale(url: string): Promise<string | undefined> {
  try {
    const { bitmap, resampled } = await decodeClampedImage(url);
    try {
      // Whether the bytes actually needed shrinking decides, not the metadata that got us
      // here: a grid tile can be handed a 128px thumbnail while its asset still reports the
      // full size, and re-encoding that would be pure loss.
      return resampled ? await encodeToObjectUrl(bitmap) : undefined;
    } finally {
      bitmap.close();
    }
  } catch (e) {
    logger.warn(`Failed to downscale image: ${e instanceof Error ? e.message : String(e)}`);
    return undefined;
  }
}

async function encodeToObjectUrl(bitmap: ImageBitmap): Promise<string | undefined> {
  const canvas = new OffscreenCanvas(bitmap.width, bitmap.height);
  const context = canvas.getContext('2d');
  if (!context) {
    return undefined;
  }
  context.drawImage(bitmap, 0, 0);
  return URL.createObjectURL(await canvas.convertToBlob({ type: REENCODE_TYPE, quality: REENCODE_QUALITY }));
}
